/**
 * The reasons Vale force-signs someone out, and the path segments that carry
 * them back from Keycloak.
 *
 * **This module deliberately has no `'use client'` directive, and that is
 * load-bearing (VALE-1859).** Both a client module (`federated-logout.ts`,
 * which builds the end-session URL in the browser) and a server component
 * (`app/(auth)/signed-out/[reason]/page.tsx`, which unpacks the segment) need
 * these values. Exported from the client module, a server component receives a
 * client-reference proxy rather than the string, and `${reason}` then
 * stringifies to something that is not header-safe: the landing page answered
 * `500 TypeError: Invalid character in header content ["location"]` on exactly
 * the two URLs sign-out sends, while the unrecognised-segment case kept working
 * because it never interpolated one. Keep this module free of hooks, browser
 * globals and the directive.
 */

/**
 * Marker that tells the /login page to render the "Signed out" card (manual
 * re-sign-in) instead of silently auto-redirecting back to Keycloak.
 */
export const SESSION_INVALID_ERROR = 'SessionInvalid';

/**
 * Narrower sibling of {@link SESSION_INVALID_ERROR}: the identity authenticated
 * fine at Keycloak but has no law-firm user behind it, so the account was never
 * provisioned for this firm. The /login page renders an "Account not set up"
 * card for this, because telling the user to sign in again (what the generic
 * card says) would only bounce them straight back here. See LF-5950.
 */
export const ACCOUNT_NOT_PROVISIONED_ERROR = 'AccountNotProvisioned';

/**
 * The reasons a federated logout can be triggered for. Both land the user on
 * their own /login card, so the set is closed and every member needs a
 * post-logout landing segment below.
 */
export type FederatedLogoutReason =
  | typeof SESSION_INVALID_ERROR
  | typeof ACCOUNT_NOT_PROVISIONED_ERROR;

/**
 * Path segment carrying each reason through Keycloak's end-session endpoint.
 *
 * **The reason cannot travel as a query parameter, and this is not a style
 * choice.** Keycloak refuses any `post_logout_redirect_uri` carrying a query
 * string or a fragment, whatever the client's post-logout allow-list says, and
 * that includes an entry written as a verbatim literal of the destination
 * (measured against `quay.io/keycloak/keycloak:26.7.3`, the image Vale deploys,
 * in VALE-1859). `/login?error=SessionInvalid` is a 400 against `/*`, against
 * `/login*`, against `/login?error=*`, against `/*?*`, and against an exact
 * `/login?error=SessionInvalid`. A plain path is admitted, and that is the only
 * thing that is.
 */
export const SIGNED_OUT_PATH_SEGMENTS: Record<FederatedLogoutReason, string> = {
  [SESSION_INVALID_ERROR]: 'session-invalid',
  [ACCOUNT_NOT_PROVISIONED_ERROR]: 'account-not-provisioned'
};

/**
 * Reverse of {@link SIGNED_OUT_PATH_SEGMENTS}, read by the landing route.
 *
 * Declared rather than derived so the spec can reconcile the two in both
 * directions and fail on a member added to one and not the other. A reason with
 * no segment here strands the user on a bare /login with no card.
 */
export const SIGNED_OUT_REASON_BY_SEGMENT: Record<
  string,
  FederatedLogoutReason
> = {
  'session-invalid': SESSION_INVALID_ERROR,
  'account-not-provisioned': ACCOUNT_NOT_PROVISIONED_ERROR
};

/**
 * The query-free, Keycloak-admissible landing path for a logout reason.
 *
 * Relative, so it is also a valid local navigation target.
 */
export function signedOutLandingPath(reason: FederatedLogoutReason): string {
  return `/signed-out/${SIGNED_OUT_PATH_SEGMENTS[reason]}`;
}
